import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { movies, movieCategories } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                <i className="ri-error-warning-line text-foreground-600 text-3xl"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-100 mb-2">影片未找到</h1>
              <p className="text-sm text-foreground-500 mb-6">抱歉，该影片可能已下架或不存在</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line"></i> 返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const catInfo = movieCategories.find((c) => c.key === movie.category);
  const categoryName = catInfo?.name || movie.category;
  const related = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 6);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName, href: `/category/${movie.category}` },
    { label: movie.title, href: `/article/${movie.id}` },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: movie.title,
    description: movie.description,
    image: movie.image,
    datePublished: movie.releaseDate,
    dateModified: movie.updateDate,
    author: {
      '@type': 'Organization',
      name: '秋霞影院',
    },
    publisher: {
      '@type': 'Organization',
      name: '秋霞影院',
    },
  };

  const movieSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.image,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map((a) => ({
      '@type': 'Person',
      name: a,
    })),
    datePublished: movie.releaseDate,
    countryOfOrigin: {
      '@type': 'Country',
      name: movie.region,
    },
  };

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
            />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(movieSchema) }}
            />

            <article>
              <div className="flex flex-col lg:flex-row gap-8 mb-12">
                <div className="flex-shrink-0 w-full sm:w-[280px] lg:w-[320px] mx-auto lg:mx-0">
                  <div className="relative overflow-hidden rounded-xl aspect-[2/3]">
                    <img
                      src={movie.image}
                      alt={movie.title}
                      title={`秋霞影院 ${movie.title} 在线观看`}
                      className="w-full h-full object-cover"
                    />
                    {movie.isHot && (
                      <div className="absolute top-3 left-3">
                        <span className="px-3 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-md">
                          热门推荐
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-2">
                    {movie.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="flex items-center gap-1 text-sm text-primary-400 font-medium">
                      <i className="ri-star-fill"></i>
                      {movie.rating}
                    </span>
                    <span className="px-2 py-0.5 bg-background-100 text-foreground-400 text-xs rounded">
                      {movie.quality}
                    </span>
                    <span className="text-sm text-foreground-500">{movie.year}</span>
                    <span className="text-sm text-foreground-500">{movie.region}</span>
                    <span className="text-sm text-foreground-500">{movie.duration}</span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-secondary-100 text-secondary-600 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="space-y-3 mb-6 text-sm">
                    <div className="flex items-baseline gap-3">
                      <span className="text-foreground-500 flex-shrink-0 w-14">导演：</span>
                      <span className="text-foreground-200">{movie.director}</span>
                    </div>
                    <div className="flex items-baseline gap-3">
                      <span className="text-foreground-500 flex-shrink-0 w-14">主演：</span>
                      <span className="text-foreground-200">{movie.actors.join('、')}</span>
                    </div>
                    <div className="flex items-baseline gap-3">
                      <span className="text-foreground-500 flex-shrink-0 w-14">地区：</span>
                      <span className="text-foreground-200">{movie.region}</span>
                    </div>
                    <div className="flex items-baseline gap-3">
                      <span className="text-foreground-500 flex-shrink-0 w-14">更新：</span>
                      <span className="text-foreground-200">{movie.updateDate}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button className="px-8 py-3 bg-accent-500 hover:bg-accent-600 text-background-50 font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer text-sm md:text-base">
                      <i className="ri-play-circle-fill mr-2"></i>
                      立即播放
                    </button>
                    <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer">
                      <i className="ri-heart-line text-foreground-400 text-lg"></i>
                    </button>
                    <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer">
                      <i className="ri-share-forward-line text-foreground-400 text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-lg font-heading font-bold text-foreground-50 mb-4 flex items-center gap-2">
                  <i className="ri-file-text-line text-accent-400"></i>
                  剧情简介
                </h2>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  {movie.description}
                </p>
              </div>

              {related.length > 0 && (
                <div>
                  <h2 className="text-lg font-heading font-bold text-foreground-50 mb-6 flex items-center gap-2">
                    <i className="ri-links-line text-accent-400"></i>
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {related.map((m) => (
                      <Link
                        key={m.id}
                        to={`/article/${m.id}`}
                        className="group cursor-pointer"
                        data-product-shop
                      >
                        <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-3">
                          <img
                            src={m.image}
                            alt={m.title}
                            title={`秋霞影院 ${m.title}`}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-background-50/90 via-transparent to-transparent"></div>
                          <div className="absolute bottom-2 right-2">
                            <span className="text-xs text-foreground-300 bg-background-50/80 px-1.5 py-0.5 rounded">
                              ★ {m.rating}
                            </span>
                          </div>
                        </div>
                        <h3 className="text-xs font-medium text-foreground-100 group-hover:text-accent-400 transition-colors truncate">
                          {m.title}
                        </h3>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </article>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}